import { useState, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Home,
  Package,
  MessageSquare,
  FileSearch,
  CheckSquare,
  UserCheck,
  FileText,
  PhoneCall,
  Tv,
  PanelLeftClose,
  PanelLeftOpen,
} from 'lucide-react';
import { useDockBadges } from './DockBadgesContext';

interface DockItem {
  id: string;
  labelKey: string;
  icon: React.ElementType;
  path: string;
  color: string; // hex color from ModuleContext
  badgeKey?: 'stock' | 'team' | 'docs' | 'tasks' | 'rh' | 'redactia';
}

const DOCK_ITEMS: DockItem[] = [
  { id: 'home', labelKey: 'dock.home', icon: Home, path: '/', color: '#6b7280' },
  { id: 'stock', labelKey: 'dock.stock', icon: Package, path: '/stock', color: '#0d9488', badgeKey: 'stock' },
  { id: 'zilloteam', labelKey: 'dock.team', icon: MessageSquare, path: '/zilloteam', color: '#8b5cf6', badgeKey: 'team' },
  { id: 'zillodocs', labelKey: 'dock.docs', icon: FileSearch, path: '/zillodocs', color: '#f59e0b', badgeKey: 'docs' },
  { id: 'zillotasks', labelKey: 'dock.tasks', icon: CheckSquare, path: '/zillotasks', color: '#7c3aed', badgeKey: 'tasks' },
  { id: 'equipe', labelKey: 'dock.rh', icon: UserCheck, path: '/equipe', color: '#10b981', badgeKey: 'rh' },
  { id: 'redactia', labelKey: 'dock.redactia', icon: FileText, path: '/redactia', color: '#7c3aed', badgeKey: 'redactia' },
  { id: 'repondeur', labelKey: 'dock.repondeur', icon: PhoneCall, path: '/repondeur', color: '#0ea5e9' },
  { id: 'zillotv', labelKey: 'dock.zillotv', icon: Tv, path: '/zillo-tv', color: '#10b981' },
];

const DOCK_WIDTH = 68; // px
const HOVER_ZONE = 8;  // px — invisible trigger zone on left edge
const HIDE_DELAY = 300; // ms — delay before hiding dock on mouse leave
const STORAGE_KEY = 'dock-autohide';

export function AppDock() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('layout');
  const { badges } = useDockBadges();

  // Auto-hide is user-controlled via toggle, persisted in localStorage
  const [autoHide, setAutoHide] = useState(() => localStorage.getItem(STORAGE_KEY) === 'true');
  const [visible, setVisible] = useState(false);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const dockRef = useRef<HTMLDivElement>(null);

  const toggleAutoHide = useCallback(() => {
    setAutoHide(prev => {
      const next = !prev;
      localStorage.setItem(STORAGE_KEY, String(next));
      if (!next) setVisible(false); // reset hover state when disabling
      return next;
    });
  }, []);

  // Show on hover zone (left edge)
  const handleEdgeEnter = () => {
    if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    setVisible(true);
  };

  const handleDockEnter = () => {
    if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    if (autoHide) setVisible(true);
  };

  const handleDockLeave = () => {
    if (!autoHide) return;
    hideTimeoutRef.current = setTimeout(() => setVisible(false), HIDE_DELAY);
  };

  // Determine active module from current path
  const getActiveModule = () => {
    if (location.pathname === '/') return 'home';
    for (const item of DOCK_ITEMS) {
      if (item.path !== '/' && location.pathname.startsWith(item.path)) return item.id;
    }
    return null;
  };

  const activeModule = getActiveModule();
  const isVisible = !autoHide || visible;

  return (
    <>
      {/* Invisible hover trigger zone on left edge (only when auto-hiding) */}
      {autoHide && !visible && (
        <div
          className="fixed left-0 top-[60px] bottom-0 z-50 md:block hidden"
          style={{ width: HOVER_ZONE }}
          onMouseEnter={handleEdgeEnter}
        />
      )}

      {/* Dock container */}
      <div
        ref={dockRef}
        onMouseEnter={handleDockEnter}
        onMouseLeave={handleDockLeave}
        className={`
          hidden md:flex flex-col items-center flex-shrink-0
          bg-gray-100 dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700
          transition-transform duration-300 ease-in-out
          ${autoHide ? 'fixed left-0 top-[60px] bottom-0 z-40' : 'relative'}
          ${autoHide && !visible ? '-translate-x-full' : 'translate-x-0'}
        `}
        style={{ width: DOCK_WIDTH }}
      >
        {/* Top spacer */}
        <div className="pt-3" />

        {/* Dock items */}
        <div className="flex-1 flex flex-col items-center gap-1 px-1.5">
          {DOCK_ITEMS.map((item) => {
            const Icon = item.icon;
            const badgeCount = item.badgeKey ? badges[item.badgeKey] : 0;
            const isActive = activeModule === item.id;

            return (
              <button
                key={item.id}
                onClick={() => navigate(item.path)}
                className={`
                  group relative flex flex-col items-center justify-center
                  w-[52px] h-[52px] rounded-xl transition-all duration-150
                  hover:scale-110 active:scale-95
                  ${isActive
                    ? 'shadow-sm ring-1 ring-gray-200 dark:ring-gray-600'
                    : 'hover:bg-gray-200/70 dark:hover:bg-gray-800'
                  }
                `}
                style={isActive ? { backgroundColor: `${item.color}15` } : undefined}
                title={t(item.labelKey)}
              >
                {/* Icon */}
                <Icon
                  size={26}
                  style={{ color: item.color }}
                  className={isActive ? '' : 'opacity-60 dark:opacity-80 group-hover:opacity-100 transition-opacity'}
                />

                {/* Label */}
                <span
                  className={`
                    text-[9px] leading-tight mt-0.5 font-medium transition-opacity
                    ${isActive
                      ? 'text-gray-900 dark:text-gray-100'
                      : 'text-gray-500 dark:text-gray-400 opacity-80 group-hover:opacity-100'
                    }
                  `}
                >
                  {t(item.labelKey)}
                </span>

                {/* Badge */}
                {badgeCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full px-1 shadow-sm">
                    {badgeCount > 99 ? '99+' : badgeCount}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Auto-hide toggle — pinned at bottom */}
        <div className="flex-shrink-0 pb-3 pt-1 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={toggleAutoHide}
            className={`
              group flex flex-col items-center justify-center
              w-[52px] h-[52px] mx-auto rounded-xl transition-all duration-150
              hover:scale-110 active:scale-95
              ${autoHide
                ? 'bg-amber-50 dark:bg-amber-900/20 ring-1 ring-amber-200 dark:ring-amber-800'
                : 'hover:bg-gray-200/70 dark:hover:bg-gray-800'
              }
            `}
            title={autoHide ? t('dock.pinTooltip') : t('dock.hideTooltip')}
          >
            {autoHide ? (
              <PanelLeftOpen
                size={22}
                className="text-amber-600 dark:text-amber-400"
              />
            ) : (
              <PanelLeftClose
                size={22}
                className="text-gray-500 dark:text-gray-400 opacity-60 group-hover:opacity-100 transition-opacity"
              />
            )}
            <span
              className={`
                text-[9px] leading-tight mt-0.5 font-medium transition-opacity
                ${autoHide
                  ? 'text-amber-700 dark:text-amber-300'
                  : 'text-gray-500 dark:text-gray-400 opacity-80 group-hover:opacity-100'
                }
              `}
            >
              {autoHide ? t('dock.pin') : t('dock.hide')}
            </span>
          </button>
        </div>
      </div>
    </>
  );
}
